import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { FetchPolicy } from '@core/enums/fetch-policy-apollo.enum';
import {
  CourierBalanceFilter,
  CouriersBalancesListResponse,
  EncashmentTransactionsListAdminResponse,
  TotalBalanceFilterDto,
} from '@core/interfaces/courier-balance.interface';
import { GetListParams } from '@core/interfaces/get-list.interface';
import { Apollo, gql } from 'apollo-angular';
import dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root',
})
export class CourierBalanceService {
  constructor(
    private apollo: Apollo,
    private http: HttpClient,
    private tokenService: TokenService,
  ) {}

  public getEncashmentTransactions(
    params: GetListParams<CourierBalanceFilter>,
  ): Observable<ApolloQueryResult<EncashmentTransactionsListAdminResponse>> {
    return this.apollo.query<EncashmentTransactionsListAdminResponse>({
      query: gql`
        query GetEncashmentTransactions($data: EncashmentTransactionsListAdminArgsDto!) {
          encashmentTransactionsListAdmin(data: $data) {
            result {
              id
              amount
              courier {
                name
                phone
                archivedAt
              }
              licenseTranslation {
                licenseLegalName
              }
              date
              operationType
              purpose
              order {
                srcOrderId
                orderId
                id
              }
            }
            total
          }
        }
      `,
      variables: {
        data: {
          filter: params.filter,
          pagination: params.pagination,
          sort: params.sort,
        },
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public getCouriersBalancesList(
    params: GetListParams<CourierBalanceFilter>,
  ): Observable<ApolloQueryResult<CouriersBalancesListResponse>> {
    return this.apollo.query<CouriersBalancesListResponse>({
      query: gql`
        query GetCouriersBalancesList($data: CouriersBalancesListArgsDto!) {
          couriersBalanceList(data: $data) {
            result {
              id
              balance
              courierService
              name
              phone
            }
            total
          }
        }
      `,
      variables: {
        data: {
          filter: params.filter,
          pagination: params.pagination,
          sort: params.sort,
        },
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public getCouriersTotalBalance(
    filter?: TotalBalanceFilterDto,
  ): Observable<ApolloQueryResult<{ couriersTotalBalance: number }>> {
    return this.apollo.query<{ couriersTotalBalance: number }>({
      query: gql`
        query GetCouriersTotalBalance($data: TotalBalanceFilterDto!) {
          couriersTotalBalance(data: $data)
        }
      `,
      variables: {
        data: filter || {},
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public exportPaymentReport(from: string, to: string): Observable<Blob> {
    const params = new HttpParams().set('date[from]', from).set('date[to]', to);

    return this.http.get(`${environment.serverUrl}order/payment-report/export`, {
      params,
      responseType: 'blob',
    });
  }

  public exportEncashment(filters: CourierBalanceFilter): Observable<Blob> {
    let params = new HttpParams();

    if (filters.search) {
      params = params.set('search', filters.search);
    }

    if (filters.courierId != null) {
      params = params.set('courierId', filters.courierId.toString());
    }

    if (filters.type) {
      params = params.set('type', filters.type);
    }

    if (filters.date?.from) {
      params = params.set('date[from]', dayjs(filters.date.from).toISOString());
    }

    if (filters.date?.to) {
      params = params.set('date[to]', dayjs(filters.date.to).toISOString());
    }

    return this.http.get(`${environment.serverUrl}order/encashment/export`, {
      params,
      responseType: 'blob',
    });
  }
}
